// composables/useLoadingError.ts
import { ref, computed } from 'vue';

interface ErrorDetails {
  type: 'timeout' | 'server_error' | 'client_error' | 'network_error' | 'unknown_error';
  suggestions: string[];
}

export function useLoadingError() {
  const loading = ref(false);
  const loadingMsg = ref('');
  const loadingProgress = ref(0);
  const currentLoadingStep = ref(0);
  const startTime = ref<number | null>(null);
  const estimatedTime = ref(30);
  
  const error = ref(false);
  const errorMsg = ref('');
  const errorDetails = ref<ErrorDetails | null>(null);
  const showErrorDetails = ref(false);
  const retryCount = ref(0);
  const maxRetries = ref(3);
  
  const connectionTimeout = ref<NodeJS.Timeout | null>(null);
  const timeoutDuration = ref(160000);

  const loadingSteps = [
    'Initializing...',
    'Processing requirements...',
    'Fetching minor data...',
    'Merging requirements...',
    'Generating plan...',
    'Finalizing...'
  ];

  const canRetry = computed(() => retryCount.value < maxRetries.value);

  const startLoadingWithProgress = () => {
    loading.value = true;
    error.value = false;
    loadingProgress.value = 0;
    currentLoadingStep.value = 0;
    retryCount.value = 0;
    startTime.value = Date.now();
    loadingMsg.value = loadingSteps[0];
    animateProgress();
  };

  const updateLoadingProgress = (step: number, message: string) => {
    currentLoadingStep.value = step;
    loadingMsg.value = message;
    loadingProgress.value = Math.min(95, (step / loadingSteps.length) * 100);
  };

  const animateProgress = () => {
    if (!loading.value || !startTime.value) return;

    const elapsed = (Date.now() - startTime.value) / 1000;
    const progressByTime = Math.min(90, (elapsed / estimatedTime.value) / 100);

    loadingProgress.value = Math.max(loadingProgress.value, progressByTime);

    const remaining = Math.max(0, estimatedTime.value - elapsed);
    const baseMessage = loadingSteps[currentLoadingStep.value] || 'Processing...';

    if (remaining > 0) {
      loadingMsg.value = `${baseMessage} (${Math.ceil(remaining)}s remaining)`;
    } else {
      loadingMsg.value = baseMessage;
    }

    setTimeout(() => animateProgress(), 1000);
  };

  const completeLoading = () => {
    loadingProgress.value = 100;
    loading.value = false;
    if (connectionTimeout.value) {
      clearTimeout(connectionTimeout.value);
      connectionTimeout.value = null;
    }
  };

  const handleError = (err: any) => {
    console.error("Operation failed:", err);
    loading.value = false;
    error.value = true;
    retryCount.value++;

    if (err.response?.status === 504) {
      errorMsg.value = "The server is taking longer than usual to process your request.";
      errorDetails.value = {
        type: 'timeout',
        suggestions: [
          'The server may be under heavy load',
          'Complex degree plans take more processing time',
          'Try again in a few minutes',
          'Consider a different selection if the problem persists'
        ]
      };
    } else if (err.response?.status === 500) {
      errorMsg.value = "Server error occurred while processing your request.";
      errorDetails.value = {
        type: 'server_error',
        suggestions: [
          'This might be a temporary server issue',
          'Try refreshing the page and starting over',
          'Check if your selections are valid'
        ]
      };
    } else if (err.response?.status === 400) {
      errorMsg.value = "Invalid request data. Please check your selections.";
      errorDetails.value = {
        type: 'client_error',
        suggestions: [
          'Verify that your selections are valid',
          'Make sure all required fields are filled',
          'Try different selections'
        ]
      };
    } else if (!navigator.onLine) {
      errorMsg.value = "No internet connection. Please check your network.";
      errorDetails.value = {
        type: 'network_error',
        suggestions: [
          'Check your internet connection',
          'Try refreshing the page',
          'Contact IT support if the problem persists'
        ]
      };
    } else {
      errorMsg.value = err.message || "An unexpected error occurred";
      errorDetails.value = {
        type: 'unknown_error',
        suggestions: [
          'Try refreshing the page',
          'Clear your browser cache',
          'Try a different browser',
          'Contact support if the problem persists'
        ]
      };
    }
  };

  const resetError = () => {
    error.value = false;
    errorMsg.value = '';
    errorDetails.value = null;
    showErrorDetails.value = false;
  };

  const toggleErrorDetails = () => {
    showErrorDetails.value = !showErrorDetails.value;
  };

  const delay = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  return {
    // State
    loading,
    loadingMsg,
    loadingProgress,
    currentLoadingStep,
    loadingSteps,
    error,
    errorMsg,
    errorDetails,
    showErrorDetails,
    retryCount,
    maxRetries,
    canRetry,
    connectionTimeout,
    timeoutDuration,
    
    // Methods
    startLoadingWithProgress,
    updateLoadingProgress,
    completeLoading,
    handleError,
    resetError,
    toggleErrorDetails,
    delay,
  };
}